<template>
  <b-table
    class="product-variants table-responsive mt-1"
    :items="variants"
    :fields="headings"
    stacked="md"
    responsive
  >
    <template #head(color)>
      <span class="product-span-color">Color/Size</span>
    </template>
    <template
      v-for="(heading, index) in headings"
      v-slot:[`cell(${heading})`]="variant"
    >
      <div :key="`${heading}${index}`">
        <template v-if="heading === COLOR">
          {{ variant.item[heading] }}
        </template>
        <!--
          Guard against ragged variant data: headings is the UNION of all sizes
          across every colour, but a colour only has cells for the sizes it
          actually offers (getVariants). For a size this colour doesn't have,
          variant.item[heading] is undefined — dereferencing .skuId/.value/etc.
          below would throw and break the whole table render (KP-3518). Render
          an empty cell instead.
        -->
        <div
          v-if="heading !== COLOR && variant.item[heading]"
          class="d-inline-flex justify-content-center flex-column"
        >
          <b-form-input
            :key="variant.item[heading].skuId"
            v-model.number="variant.item[heading].value"
            type="number"
            :class="getInputClasses(variant, heading)"
            min="0"
            :max="getMaxQuantity(variant.item[heading])"
            class="w-75 zero-qty-error"
            :readonly="isDisabled || (isProductValueDisabled(variant.item[heading].quantityAvailable) && variant.item[heading].maxShipmentQty == null)"
            @input="onQuantityInput(variant.item[heading])"
          />
          <div
            v-if="shouldShowMaxQty(variant, heading)"
            class="w-75 text-nowrap text-center color-actions-text-grey mt-2-px"
          >
            Max Qty: {{ getDisplayMaxQty(variant.item[heading]) }}
          </div>
        </div>
      </div>
    </template>
  </b-table>
</template>
<script>
import { BFormInput, BTable } from 'bootstrap-vue'
import UserRoleMixinVue from '../../UserRoleMixin.vue'
import ProductVariantsMixinVue from './ProductVariantsMixin.vue'
import {
  hasShipmentMaxQty as hasShipmentMaxQtyUtil,
  isQuantityExceedError as isQuantityExceedErrorUtil,
  isShipmentQtyExceedError as isShipmentQtyExceedErrorUtil,
  getDisplayMaxQtyValue,
} from './productVariantValidation'

export default {
  name: 'ProductVariants',
  components: {
    BTable,
    BFormInput,
  },
  mixins: [UserRoleMixinVue, ProductVariantsMixinVue],
  props: {
    isShipmentSettingsModal: {
      type: Boolean,
      default: false,
    },
    showQuantityExceedValidation: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    hasShipmentMaxQty(item) {
      return hasShipmentMaxQtyUtil(this.isShipmentSettingsModal, item)
    },
    shouldShowMaxQty(variant, heading) {
      if (this.isShipmentSettingsModal && this.isCarton) return false
      const item = variant?.item?.[heading]
      if (!item) return false
      if (this.hasShipmentMaxQty(item)) return true
      if (!this.canShowMaxQty) return false
      return this.hasProductMaxQuantity(item.quantityAvailable)
    },
    getInputClasses(variant, heading) {
      const item = variant?.item?.[heading]
      if (!item) return {}
      const isDecimalError = !Number.isInteger(item.value)
      const isMinQtyError = !this.isCarton && item.value != null && item.value < this.minQtyPerSize
      const isMaxQtyError = isQuantityExceedErrorUtil({
        showValidation: this.showQuantityExceedValidation,
        isCarton: this.isCarton,
        hasShipmentMax: this.hasShipmentMaxQty(item),
        isProductItemValid: this.isProductItemValid,
        item,
      })
      const isShipmentQtyError = isShipmentQtyExceedErrorUtil(this.showQuantityExceedValidation, item)
      return {
        'decimal-qty-error': isDecimalError,
        invalid: isMinQtyError || isMaxQtyError || isShipmentQtyError,
      }
    },
    getDisplayMaxQty(item) {
      return getDisplayMaxQtyValue(this.hasShipmentMaxQty(item), item)
    },
    getMaxQuantity(item) {
      if (item.maxShipmentQty !== undefined) return item.maxShipmentQty
      if (this.canShowMaxQty && item.quantityAvailable !== undefined) return item.quantityAvailable
      return undefined
    },
    onQuantityInput(item) {
      if (typeof item.value === 'number' && item.value < 0) item.value = 0
      this.$forceUpdate()
      this.$emit('update-variants', this.variants)
    },
  },
}
</script>

<style lang="scss">
@import "src/@core/scss/base/bootstrap-extended/_variables.scss";

.product-variants {
  input.form-control {
    min-width: 50px;
  }
  input.form-control.invalid {
    border-color: $red !important;
    border-width: 2px !important;
    &:focus {
      border-color: $red !important;
      box-shadow: 0 0 0 0.2rem rgba($red, 0.25) !important;
    }
  }
  th:first-child,
  td:first-child {
    position: sticky;
    left: 0;
    z-index: 9;
  }
  td {
    padding: 1rem;
  }
}

.product-span-color {
  text-transform: capitalize;
  width: 80px;
  display: block;
}

.has-error .invalid {
  border-color: $red;
  &:focus {
    border-color: $red;
  }
}
</style>
